/* 0.47.41 - A Layer's background video.
 *
 * Always muted (browsers only start a video by themselves when it has
 * no sound) and inline on phones. Playback, from the Layer:
 *   bg_video_plays  0 or unset = loop for ever, 1 = once (stops on its
 *                   last frame), 2 or 3 = that many times
 * A visitor who asks for less motion (prefers-reduced-motion) gets the
 * first frame, still, as the platform's other animations respect it.
 *
 * 0.47.42 - Phones. React sets `muted` as a property only, never as the
 * attribute, and iOS Safari will not start a video by itself unless the
 * attribute is there when it loads, so the video stayed blank on iPhones.
 * The attribute is now set on the element before play() is asked for.
 * And `#t=0.001` makes Safari draw the first frame even where autoplay
 * is refused (Low Power Mode), instead of an empty box.
 *
 * 0.47.44 - Phones that ask for less motion (iOS Reduce Motion, Android
 * Remove animations, often on to save battery) got the still first frame
 * while other sites played the same file. The still frame is now the
 * Layer's choice (bg_video_reduce_still), off by default: the video
 * plays. And when a browser refuses to start it, the first touch, click,
 * scroll or key press anywhere on the page starts it. */
import { useEffect, useRef } from 'react';

export function videoPlaysOf(layer) {
  const n = Number(layer && layer.bg_video_plays);
  return [1, 2, 3].includes(n) ? n : 0;
}

function reducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/* The address with a start time, so a first frame is drawn. */
function withFirstFrame(src) {
  if (!src || src.startsWith('data:') || src.includes('#')) return src;
  return `${src}#t=0.001`;
}

export default function BandVideo({ layer, style }) {
  const ref = useRef(null);
  const played = useRef(0);
  const plays = videoPlaysOf(layer);
  const still = !!(layer && layer.bg_video_reduce_still) && reducedMotion();
  const src = (layer && layer.bg_media_src) || '';

  useEffect(() => {
    played.current = 0;
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    if (still) return undefined;
    const events = ['touchstart', 'pointerdown', 'click', 'scroll', 'keydown'];
    let waiting = false;
    const retry = () => {
      if (!ref.current) return;
      ref.current.play().then(stopWaiting).catch(() => {});
    };
    const stopWaiting = () => {
      if (!waiting) return;
      waiting = false;
      events.forEach(ev => window.removeEventListener(ev, retry, true));
    };
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(stopWaiting).catch(() => {
          // Refused (a power saving mode, a strict browser): start on the
          // visitor's first gesture instead.
          if (waiting) return;
          waiting = true;
          events.forEach(ev => window.addEventListener(ev, retry, { capture: true, passive: true }));
        });
      }
    };
    tryPlay();
    // Some phones refuse a play() asked before enough has loaded.
    const onCanPlay = () => { if (v.paused && !v.ended) tryPlay(); };
    v.addEventListener('canplay', onCanPlay);
    return () => {
      v.removeEventListener('canplay', onCanPlay);
      waiting = true; stopWaiting();
    };
  }, [src, plays, still]);

  const onEnded = () => {
    played.current += 1;
    if (plays > 1 && played.current < plays && ref.current) {
      ref.current.currentTime = 0;
      ref.current.play().catch(() => {});
    }
  };

  return (
    <video
      ref={ref}
      key={`${src}|${plays}`}
      src={withFirstFrame(src)}
      autoPlay={!still}
      loop={!still && plays === 0}
      muted
      playsInline
      preload="auto"
      onEnded={onEnded}
      aria-hidden="true"
      tabIndex={-1}
      style={style}
    />
  );
}

/* 0.47.42 / 0.47.43 - The band's own shape follows the media's, when the
 * Layer asks for it (bg_fit_media): the band is at least as tall as the
 * picture or video at the screen's width, and never taller than
 * bg_media_max_height (px) for that reason. Content taller than that
 * still makes it taller.
 *
 * Drawn with an invisible float whose top padding is a share of the
 * band's width (the classic ratio box), not with aspect-ratio: the band
 * clips its media (overflow: hidden), and with aspect-ratio that also
 * clipped content taller than the ratio. The ratio (width / height) is
 * measured in the editor and kept as bg_media_ratio. */
/* 0.47.46 - The band's height per screen size: PC (991px and up),
 * Tablet (768px and up) and Mobile, each As its content or The media's
 * shape with its own maximum.
 *
 *   PC      bg_fit_media (true = shape) and bg_media_max_height
 *   Tablet  bg_fit_md ('content' | 'shape' | unset = as PC) and _md
 *   Mobile  bg_fit_xs (unset = as Tablet) and _xs
 *
 * Unset follows the next larger screen, maximum included. A maximum on
 * an unset screen (0.47.45) means the shape with that maximum. A screen
 * set to the shape with no maximum has no limit. */
export function bandShapeTiers(layer) {
  const px = (v) => (Number(v) > 0 ? Math.round(Number(v)) : 0);
  const L = layer || {};
  const lg = { shape: !!L.bg_fit_media, max: L.bg_fit_media ? px(L.bg_media_max_height) : 0 };
  const tier = (mode, max, larger) => {
    if (mode === 'content') return { shape: false, max: 0 };
    if (mode === 'shape') return { shape: true, max: px(max) };
    if (px(max)) return { shape: true, max: px(max) };
    return larger;
  };
  const md = tier(L.bg_fit_md, L.bg_media_max_height_md, lg);
  const xs = tier(L.bg_fit_xs, L.bg_media_max_height_xs, md);
  return { lg, md, xs, any: lg.shape || md.shape || xs.shape };
}

export function bandMediaSpacer(layer) {
  if (!layer || !layer.bg_media_src) return null;
  const t = bandShapeTiers(layer);
  if (!t.any) return null;
  const r = Number(layer.bg_media_ratio);
  if (!(r > 0.1 && r < 10)) return null;
  const pct = `${(100 / r).toFixed(4)}%`;
  const vars = {};
  for (const k of ['xs', 'md', 'lg']) {
    vars[`--bs-pct-${k}`] = t[k].shape ? pct : '0%';
    vars[`--bs-max-${k}`] = t[k].max ? `${t[k].max}px` : '100000px';
  }
  return <div aria-hidden="true" className="hen-band-shape" style={vars} />;
}

/* What stays in view when the band is shorter than the media (a
 * maximum height, or content wider than tall): bg_media_crop
 *   'center' or unset  cut equally above and below
 *   'top'              keep the top, cut below
 *   'bottom'           keep the bottom, cut above */
export function bandMediaFocus(layer) {
  const c = layer && layer.bg_media_crop;
  return c === 'top' ? 'center top' : c === 'bottom' ? 'center bottom' : 'center center';
}
